import { Component, createRef } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import styled, { keyframes } from "styled-components";
import { MovableCardWrapper, CardHeader } from "../styles/Base";
import FooterCard from "./Card/FooterCard";
import TrashIcon from "../../icons/Trash";
import CardTitle from "./CardTitle";
import BoardCard from "./BoardCard";
import { ContentTypes } from "../../constants/action-types";
import CardContextMenu from "./CardContextMenu";
import CardSelectionCheckbox from "./CardSelectionCheckbox";

const calendarAnimation = keyframes`
 0% { transform: translate3d(0px, -10px, 0px);pointer-events: none;}
 100% { transform: translate3d(0px, 0px, 0px); pointer-events: auto;}
`;

const ListOptionWrapper = styled.div`
  position: absolute;
  top: 25px;
  background-color: #ffffff;
  border: solid 1px #f3f2f1;
  border-radius: 2px;
  box-sizing: border-box;
  box-shadow: rgb(0 0 0 / 11%) 0px 0.6px 1.8px 0px;
  animation-name: ${calendarAnimation};
  animation-duration: 0.367s;
  animation-timing-function: cubic-bezier(0.1, 0.9, 0.2, 1);
  animation-fill-mode: both;
  z-index: 1;
  width: 100%;
  min-width: 170px;
  right: 0px;
`;

const OptionsWrapper = styled.div`
  position: absolute;
  right: 6px;
  top: 8px;
  z-index: 1;
`;

const DropdownItem = styled.div`
  width: 100%;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 8px;
  &:hover {
    background-color: rgb(244, 244, 244);
  }

  svg {
    margin-right: 5px;
  }
`;

export const CardHeaderMenu = styled.div`
  flex-shrink: 0;
  margin-left: auto;
  .btn-action {
    display: none;
  }
  &:hover .btn-action {
    display: flex;
    color: #2563eb;
  }
`;

class Card extends Component {
  constructor(props) {
    super(props);
    this.dropdownRef = createRef();
  }
  state = {
    isShowOptions: false,
    contextMenu: null, // { x, y } for right-click context menu
  };

  updateCard = (card) => {
    const { id, onChange } = this.props;
    onChange({ ...card, id });
  };

  onSelectedItem = (value) => {
    switch (value) {
      case "assign":
      case "copy":
        break;
      case "delete":
        this.props.onDelete();
        break;

      default:
        break;
    }
    this.setState({ isShowOptions: false });
  };

  handleClickOut = (event) => {
    const { target } = event;
    if (!this.dropdownRef || !this.dropdownRef.current) return;
    if (!this.dropdownRef.current.contains(target)) {
      this.setState({ isShowOptions: false, contextMenu: null });
    }
  };

  handleContextMenu = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.setState({
      contextMenu: { x: event.clientX, y: event.clientY },
    });
  };

  handleContextMenuClose = () => {
    this.setState({ contextMenu: null });
  };

  componentDidMount() {
    document.addEventListener("mousedown", this.handleClickOut);
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.handleClickOut);
  }

  render() {
    const { style, className, id, title, task, users, isJira } = this.props;
    const { isShowOptions } = this.state;
    const menus = [{ value: "delete", label: "Delete", icon: <TrashIcon size={18} /> }];

    const renderBoard = () => {
      if (task.get("type") === ContentTypes.COMPANY || task.get("type") === ContentTypes.CONTACT) {
        return (
          <>
            <div style={{ position: "relative" }}>
              {isShowOptions && (
                <OptionsWrapper>
                  <ListOptionWrapper className="list-dropdown-group" ref={this.dropdownRef}>
                    {menus.map((menu) => (
                      <DropdownItem
                        className="menu-item"
                        key={menu.value}
                        onClick={() => {
                          this.onSelectedItem(menu.value);
                        }}
                      >
                        {menu.icon}
                        {menu.label}
                      </DropdownItem>
                    ))}
                  </ListOptionWrapper>
                </OptionsWrapper>
              )}
            </div>
            <BoardCard task={task} isJira={isJira} />
          </>
        );
      }
      if (task.get("type") === ContentTypes.TASK) {
        const percentComplete = parseFloat(task.getIn(["workflowData", "percentComplete"]));
        return (
          <>
            <CardHeader>
              <CardTitle title={title} task={task} />
            </CardHeader>
            <div>{percentComplete ? `${percentComplete}%` : null}</div>
            <div className="w-full my-2 border-b border-gray-200"></div>
            <FooterCard task={task} users={users} onChange={this.updateCard} />
          </>
        );
      }
      return (
        <>
          <CardHeader>
            <CardTitle title={title} task={task} />
          </CardHeader>
          <BoardCard task={task} isJira={isJira} />
          <div className="w-full my-2 border-b border-gray-200"></div>
          <FooterCard task={task} users={users} onChange={this.updateCard} />
        </>
      );
    };

    const { contextMenu } = this.state;
    return (
      <MovableCardWrapper
        data-id={id}
        style={style}
        className={`${className} group`}
        onContextMenu={this.handleContextMenu}
      >
        <CardSelectionCheckbox taskId={id} />
        {renderBoard()}
        {contextMenu && (
          <CardContextMenu
            x={contextMenu.x}
            y={contextMenu.y}
            task={task}
            onDelete={() => {
              this.onSelectedItem("delete");
              this.handleContextMenuClose();
            }}
            onClose={this.handleContextMenuClose}
          />
        )}
      </MovableCardWrapper>
    );
  }
}

Card.propTypes = {
  showDeleteButton: PropTypes.bool,
  onDelete: PropTypes.func,
  onClick: PropTypes.func,
  style: PropTypes.object,
  tagStyle: PropTypes.object,
  className: PropTypes.string,
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  label: PropTypes.string,
  description: PropTypes.string,
  tags: PropTypes.array,
};

Card.defaultProps = {
  showDeleteButton: true,
  onDelete: () => {},
  onClick: () => {},
  style: {},
  tagStyle: {},
  title: "no title",
  description: "",
  label: "",
  tags: [],
  className: "",
};

const mapStateToProps = (state) => ({
  showCardLabels: state.taskReducer.get("showCardLabels"),
});

export default connect(mapStateToProps)(Card);
